import { useMemo } from 'react';
import { usePerpMarkets } from 'src/hooks/perpMarkets/usePerpMarkets';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { AssetFilterWithSearch } from 'src/components/Inputs/AssetFilterWithSearch';
import { AssetListAssetWithMarketIndex } from 'src/@types/assets';
import { PORTFOLIO_HISTORY_MARKET_FILTER_ALL_OPTION } from 'src/components/PortfolioPage/PortfolioHistorySubPage/consts';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';

interface Props {
	id: string;
	marketType?: 'spot' | 'perp' | undefined;
	marketFilter: string;
	handleMarketFilterChange: (selectedMarket: string) => void;
}

const DEFAULT_MARKET_FILTER_OPTIONS: AssetListAssetWithMarketIndex[] = [];

export default function PortfolioHistoryTableMarketFilter({
	id,
	marketType,
	marketFilter,
	handleMarketFilterChange,
}: Props) {
	const perpMarkets = usePerpMarkets();
	const perpAssetFilterOptions = useMemo(() => {
		if (marketType === 'spot') {
			return DEFAULT_MARKET_FILTER_OPTIONS;
		}

		return perpMarkets.map((market) => ({
			ticker: market.symbol,
			displayLabel: market.symbol,
			marketIndex: market.marketIndex,
		}));
	}, [perpMarkets, marketType]);

	const currentPoolId = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.poolId
	);
	const spotMarkets = useSpotMarkets();
	const spotAssetFilterOptions = useMemo(() => {
		if (marketType === 'perp') {
			return DEFAULT_MARKET_FILTER_OPTIONS;
		}

		return spotMarkets
			.filter((market) => market.poolId === currentPoolId)
			.map((market) => ({
				mint: market.mint?.toBase58() ?? market.symbol,
				ticker: market.symbol,
				// If we're showing both perps and spot, tack on `/USDC` to better differentiate.
				displayLabel: !marketType ? `${market.symbol}/USDC` : market.symbol,
				marketIndex: market.marketIndex,
			}));
	}, [spotMarkets, marketType, currentPoolId]);

	const assetFilterOptions = useMemo(() => {
		return [
			PORTFOLIO_HISTORY_MARKET_FILTER_ALL_OPTION,
			...perpAssetFilterOptions,
			...spotAssetFilterOptions,
		];
	}, [perpAssetFilterOptions, spotAssetFilterOptions]);

	return (
		<AssetFilterWithSearch
			id={id}
			label="Market"
			className="min-w-50 sm:max-w-58"
			scrollContainerClassName="max-h-50"
			currentSelection={marketFilter}
			options={assetFilterOptions}
			onChange={handleMarketFilterChange}
		/>
	);
}
